import useSWR from 'swr';
import { getOfflineCampaigns, getOfflinePreviewCampaigns, getOnlineCampaigns } from '@api/interfaces/promotionApi';
import { useSearchParams } from 'next/navigation';
import Config from '@config';
import useCustomer from '@hooks/useCustomer';
import { parseDateQuery } from '@helpers/formatDate';
import useStore from '@hooks/useStore';
import useLocalStorage from '@hooks/useLocalStorage';
import type { AxfoodStoreInfoViewModel } from '@occ/api-client';
import useCart from '@hooks/useCart';

export const GENERAL_CAMPAIGNS = 'PERSONAL_GENERAL';
export const PERSONAL_CAMPAIGNS = 'PERSONAL_SEGMENTED';

type StoreType = 'online' | 'offline' | 'preview/offline';

type KeyType = {
  query: string;
  isAnonymous: boolean;
  storeType: StoreType;
  size?: number;
  page?: number;
  dateFrom?: string;
  dateTo?: string;
  fields?: string[];
  slot?: string;
};

const generalCampaignsFetcher = async ({
  query,
  storeType,
  size = 20,
  page = 0,
  dateFrom,
  dateTo,
  fields,
}: KeyType) => {
  switch (storeType) {
    case 'preview/offline':
      // The API expects date strings (yyyy-MM-dd) despite the client type annotation saying number.
      return await getOfflinePreviewCampaigns(
        query,
        fields,
        page,
        size,
        dateFrom as unknown as number,
        dateTo as unknown as number
      );
    case 'online':
      return await getOnlineCampaigns(query, GENERAL_CAMPAIGNS, page, size);
    case 'offline':
      return await getOfflineCampaigns(query, GENERAL_CAMPAIGNS, page, size);
  }
};

const personalCampaignsFetcher = async ({ query, storeType, size = 20 }: KeyType) => {
  switch (storeType) {
    case 'online':
      return await getOnlineCampaigns(query, PERSONAL_CAMPAIGNS, 0, size);
    case 'offline':
      return await getOfflineCampaigns(query, PERSONAL_CAMPAIGNS, 0, size);
    default:
      return null;
  }
};

const usePromotionCampaigns = (
  isPreview: boolean,
  page: number,
  showProducts: boolean,
  selectedOfflineStore: AxfoodStoreInfoViewModel,
  activeTab?: PromotionTabType
) => {
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams?.entries() || []);
  const { customer } = useCustomer();
  const { store } = useStore();
  const { cart } = useCart();

  const fields = showProducts ? ['IMAGE'] : undefined;
  const { from: dateFrom, to: dateTo } = parseDateQuery(query);
  const isStoreMode = activeTab === Config.ROUTES.promotionpage.store;
  const storeType: StoreType = isPreview ? 'preview/offline' : isStoreMode ? 'offline' : 'online';
  const [promotionStoreId] = useLocalStorage(Config.LOCAL_STORAGE_SELECTORS.promotionStoreId, '');
  // Preview always uses the online store ID — it has no tab/offline-store concept.
  const storeId =
    (isPreview ? store?.storeId : isStoreMode ? selectedOfflineStore?.storeId || promotionStoreId : store?.storeId) ||
    '';
  let q = storeId;

  if (query?.q?.length) {
    q += (query.q as string).startsWith(':') ? query.q : `:${query.q}`;
  }

  // Gate on the store, not on q: with a filter, q is ':<filter>' before the store has loaded,
  // which the API rejects with 400 and the page shows as an error.
  // For preview mode we only need a storeId — there's no activeTab, so skip that gate.
  // Wait for the customer too: isAnonymous is part of the key, so fetching before it loads fetches everything twice.
  const generalKey: KeyType | null =
    storeId && customer && (activeTab || isPreview)
      ? {
          query: q,
          isAnonymous: customer.isAnonymous as boolean,
          storeType,
          dateFrom,
          dateTo,
          fields,
          page,
          slot: cart?.slot?.code,
        }
      : null;
  const {
    data: generalCampaigns,
    error: generalError,
    isLoading: isGeneralLoading,
  } = useSWR(generalKey, generalCampaignsFetcher);

  const personalKey: KeyType | null =
    storeId && activeTab && customer && !customer.isAnonymous && storeType !== 'preview/offline'
      ? { query: q, isAnonymous: false, storeType, slot: cart?.slot?.code }
      : null;
  const { data: personalCampaigns } = useSWR(personalKey, personalCampaignsFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return {
    query: q,
    personalCampaigns,
    generalCampaigns,
    isLoading: isGeneralLoading,
    error: generalError,
  };
};

export default usePromotionCampaigns;
